import './OfflineInputsDialogs.scss';
import { useMemo, useState } from 'react';
import {
  MdCalculate,
  MdCheck,
  MdCalendarToday,
  MdDelete,
  MdSmsFailed,
  MdShowChart
} from 'react-icons/md';
import { AlertDialog } from '@Components/AlertDialog/AlertDialog';
import { Radio } from '@Components/FormCheck';
import { Intent } from '@Components/designConstants';
import {
  IncrementalityTestingTable,
  OfflineInputsEntry,
  OfflineInputsTable
} from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { EntryEditsMap, IncrementalityTestingEntryEditsMap } from './OfflineInputsUtils';

const ValidationErrorOptions = {
  REJECT: 'reject',
  RESOLVE: 'resolve'
} as const;
type ValidationErrorOption = (typeof ValidationErrorOptions)[keyof typeof ValidationErrorOptions];

interface BaseDialogProps {
  show: boolean;
  onHide: () => void;
}

interface DiscardConfirmationDialogProps extends BaseDialogProps {
  onDiscard: () => void;
}

interface DeleteConfirmationDialogProps extends BaseDialogProps {
  onDelete: () => void;
}

interface LeaveConfirmationDialogProps extends BaseDialogProps {
  onLeave: () => void;
  onStay: () => void;
}

interface ValidationErrorDialogProps {
  errorDates: string[];
  onResolve: () => void;
  onReject: () => void;
  tableEdits: EntryEditsMap;
  table?: OfflineInputsTable;
  getInputDescription: (table: OfflineInputsTable) => string;
  createEmptyEntry: (date: string) => OfflineInputsEntry;
  // Modify the entry in-place
  normalizeEntry: (table: OfflineInputsTable, entry: OfflineInputsEntry) => void;
}

interface IncrementalityTestingValidationErrorDialogProps {
  errors: string[];
  onResolve: () => void;
  onReject: () => void;
  tableEdits: IncrementalityTestingEntryEditsMap;
  table?: IncrementalityTestingTable;
  getInputDescription: (table: IncrementalityTestingTable) => string;
}

export const InstructionsDialog = ({ show, onHide }: BaseDialogProps) => (
  <AlertDialog
    show={show}
    onHide={onHide}
    icon={<MdCalculate size={95} />}
    title='Please Split Your Data Out By Day'
    body='Please disaggregate your data by day and come back to enter it in.'
    primaryAction={{ label: 'Got it!' }}
  />
);

export const IncrementalityTestingInstructionsDialog = ({ show, onHide }: BaseDialogProps) => (
  <AlertDialog
    show={show}
    onHide={onHide}
    icon={<MdCalculate size={95} />}
    title='Please Split Your Data Out By KPI'
    body='Please disaggregate your data by kpi and come back to enter it in.'
    primaryAction={{ label: 'Got it!' }}
  />
);

export const CommitSuccessDialog = ({ show, onHide }: BaseDialogProps) => (
  <AlertDialog
    intent={Intent.SUCCESS}
    show={show}
    onHide={onHide}
    icon={<MdCheck size={95} />}
    title='Success!'
    body='Your inputs have been committed and will be available in the next refresh of your MMM.'
  />
);

export const LeaveConfirmationDialog = ({
  show,
  onLeave,
  onStay,
  onHide
}: LeaveConfirmationDialogProps) => (
  <AlertDialog
    intent={Intent.ERROR}
    show={show}
    onHide={onHide}
    icon={<MdSmsFailed size={72} />}
    title='Are you sure you want to leave?'
    body='If you leave now, your changes will not be saved.'
    className='leave-confirmation-dialog'
    primaryAction={{ onClick: onStay, label: 'Stay' }}
    secondaryAction={{ onClick: onLeave, label: 'Leave' }}
  />
);

export const DiscardConfirmationDialog = ({
  show,
  onDiscard,
  onHide
}: DiscardConfirmationDialogProps) => (
  <AlertDialog
    intent={Intent.WARNING}
    show={show}
    onHide={onHide}
    icon={<MdDelete size={95} />}
    title='Discard Changes?'
    className='discard-confirmation-dialog'
    body="Any edits you've made won't be saved and you'll have to start over."
    secondaryAction={{ onClick: onHide }}
    primaryAction={{ onClick: onDiscard, label: 'Discard' }}
  />
);

export const DeleteConfirmationDialog = ({
  show,
  onDelete,
  onHide
}: DeleteConfirmationDialogProps) => (
  <AlertDialog
    intent={Intent.ERROR}
    show={show}
    onHide={onHide}
    icon={<MdDelete size={95} />}
    title='Delete Data?'
    body="All data in these columns will be deleted and you'll have to enter your data in again if you want it back."
    primaryAction={{ onClick: onDelete, label: 'Delete' }}
    secondaryAction={{ onClick: onHide }}
  />
);

export const ValidationErrorDialog = ({
  errorDates,
  onResolve,
  onReject,
  tableEdits,
  table,
  getInputDescription,
  createEmptyEntry,
  normalizeEntry
}: ValidationErrorDialogProps) => {
  const [selectedOption, setSelectedOption] = useState<ValidationErrorOption>(
    ValidationErrorOptions.REJECT
  );

  if (!table) {
    return null;
  }

  const startDate = errorDates[0].split('T')[0];
  const endDate = errorDates[errorDates.length - 1].split('T')[0];
  const datesLabel =
    errorDates.length > 1 ? `Dates: ${startDate} to ${endDate}` : `Date: ${startDate}`;
  const resolveLabel =
    errorDates.length > 1 ? "We didn't run on those dates" : "We didn't run on that date";

  return (
    <AlertDialog
      show
      escapable={false}
      onHide={() => {}}
      className='validation-error-dialog'
      icon={<MdCalendarToday size={95} />}
      title='We Need More Info About These Blank Dates'
      body={
        <>
          <div className='error-dates'>{datesLabel}</div>
          <div className='error-input'>For Input: {getInputDescription(table)}</div>{' '}
          <div className='validation-error-options'>
            <Radio
              id='resolve-radio-button'
              label={resolveLabel}
              checked={selectedOption === ValidationErrorOptions.RESOLVE}
              onChange={() => setSelectedOption(ValidationErrorOptions.RESOLVE)}
            />
            <Radio
              id='return-radio-button'
              label='We aggregated data and need to split out by day'
              checked={selectedOption === ValidationErrorOptions.REJECT}
              onChange={() => setSelectedOption(ValidationErrorOptions.REJECT)}
            />
          </div>
        </>
      }
      primaryAction={{
        onClick: () => {
          if (selectedOption === ValidationErrorOptions.RESOLVE) {
            errorDates.forEach(date => {
              let entry = tableEdits[date];
              if (!entry) {
                // tableEdits is intentionally mutated in place for the caller.
                // eslint-disable-next-line no-param-reassign
                tableEdits[date] = createEmptyEntry(date);
                entry = tableEdits[date];
              }

              normalizeEntry(table, entry);
            });
            onResolve();
          } else {
            onReject();
          }
        }
      }}
    />
  );
};

export const IncrementalityTestingValidationErrorDialog = ({
  errors,
  onResolve,
  onReject,
  tableEdits,
  table,
  getInputDescription
}: IncrementalityTestingValidationErrorDialogProps) => {
  const [selectedOption, setSelectedOption] = useState<ValidationErrorOption>(
    ValidationErrorOptions.REJECT
  );

  const errorArr: { entryId: string; kpiName: string }[] = useMemo(() => {
    if (tableEdits === undefined || tableEdits === null) {
      return [];
    }
    return errors.map(errorId => ({
      entryId: errorId,
      kpiName: tableEdits[errorId].kpiName
    }));
  }, [errors, tableEdits]);

  if (table === undefined || table === null || tableEdits === undefined || tableEdits === null) {
    return null;
  }

  const kpiLabel = `KPIs: ${errorArr.map(errorObj => errorObj.kpiName).join(', ')}`;
  const resolveLabel = "We didn't test those KPIs";

  return (
    <AlertDialog
      show
      escapable={false}
      onHide={() => {}}
      className='validation-error-dialog'
      icon={<MdShowChart size={95} />}
      title='We Need More Info About These Blank KPIs'
      body={
        <>
          <div className='error-kpis'>{kpiLabel}</div>
          <div className='error-input'>For Input: {getInputDescription(table)}</div>{' '}
          <div className='validation-error-options'>
            <Radio
              id='resolve-radio-button'
              label={resolveLabel}
              checked={selectedOption === ValidationErrorOptions.RESOLVE}
              onChange={() => setSelectedOption(ValidationErrorOptions.RESOLVE)}
            />
            <Radio
              id='return-radio-button'
              label='We aggregated data and need to split out by KPI'
              checked={selectedOption === ValidationErrorOptions.REJECT}
              onChange={() => setSelectedOption(ValidationErrorOptions.REJECT)}
            />
          </div>
        </>
      }
      primaryAction={{
        onClick: () => {
          if (selectedOption === ValidationErrorOptions.RESOLVE) {
            onResolve();
          } else {
            onReject();
          }
        }
      }}
    />
  );
};
